/**
 * Device Service
 *
 * Stores the per-device bearer token that Mon Ami GO uses to authenticate
 * against the online Mon Ami app's /api/offline/* endpoints. The token is
 * issued once by `POST /api/offline/devices` (cookie-authed) and replayed
 * as `Authorization: Bearer …` on every subsequent call.
 *
 * The value is stored in applicationSession (encrypted at rest via the
 * dexie-encrypted middleware on the `value` field) under two keys:
 *   - device_token:    the plaintext bearer token (only ever seen on the
 *                      registration response; never returned by the server
 *                      after that)
 *   - device_metadata: { id, label, registered_at, expires_at } so the GO
 *                      Settings UI can show the row and the user can rename
 *                      it without hitting the server first
 *
 * Both move together — `clearDevice()` removes both, and `setDevice()` writes
 * both in one Dexie transaction so a write failure can't leave the local
 * state half-populated (token present but no metadata, or vice versa).
 * Callers should never read or write either key directly.
 */
import db from '@/db';

export const DEVICE_TOKEN_KEY = 'device_token';
export const DEVICE_METADATA_KEY = 'device_metadata';

export interface DeviceMetadata {
  id: number;
  label: string;
  registered_at: string;
  expires_at: string;
}

export async function getDeviceToken(): Promise<string | null> {
  // Direct Dexie read, not application_session_service.getItem, because
  // getItem swallows DB errors and returns null. That's fine for routine
  // reads but wrong for registerDeviceIfNeeded's "no token → register"
  // check — a transient DatabaseClosedError during a lock/unlock window
  // would look like "no token stored" and trigger a duplicate device
  // row server-side, overwriting the still-valid bearer with a new one.
  // Callers that want the swallowing semantics can `.catch(() => null)`.
  const row = await db.applicationSession.get(DEVICE_TOKEN_KEY);
  return row ? (row.value as string) : null;
}

export async function getDeviceMetadata(): Promise<DeviceMetadata | null> {
  const row = await db.applicationSession.get(DEVICE_METADATA_KEY);
  return row ? (row.value as DeviceMetadata) : null;
}

export async function setDevice(token: string, metadata: DeviceMetadata): Promise<void> {
  const updated_at = new Date().toISOString();
  // Both rows commit together — if the transaction aborts mid-write, neither
  // is persisted, so we never end up with a token whose metadata is missing
  // (which would leave the Settings card hidden while the orphaned token
  // continued to authenticate API calls).
  await db.transaction('rw', db.applicationSession, async () => {
    await db.applicationSession.put({ key: DEVICE_TOKEN_KEY, value: token, updated_at });
    await db.applicationSession.put({ key: DEVICE_METADATA_KEY, value: metadata, updated_at });
  });
}

/**
 * Update the locally-stored metadata (label, registered_at, expires_at)
 * without touching the token. Used by the silent refresh flow, which bumps
 * the server-side expires_at while keeping the same bearer.
 *
 * Unlike setDevice(), this writes only the metadata row — so it must
 * defend the "metadata exists iff token exists" invariant manually. If
 * clearDevice() runs while a refresh is in flight (e.g. a parallel
 * fetchWithAuth gets a 401 and clears both rows), we must NOT recreate
 * the metadata row on its own, or the SecurityTab card would surface a
 * device whose token no longer exists. The token check + write run in a
 * single Dexie transaction so the read can't be invalidated by a
 * concurrent clearDevice between check and put.
 */
export async function setDeviceMetadata(metadata: DeviceMetadata): Promise<void> {
  await db.transaction('rw', db.applicationSession, async () => {
    const tokenRow = await db.applicationSession.get(DEVICE_TOKEN_KEY);
    if (!tokenRow) {
      // Token was cleared after we kicked off whatever produced this call.
      // Dropping the write preserves the invariant — the metadata stays
      // gone alongside the missing token.
      return;
    }
    await db.applicationSession.put({
      key: DEVICE_METADATA_KEY,
      value: metadata,
      updated_at: new Date().toISOString(),
    });
  });
}

export async function clearDevice(): Promise<void> {
  await db.transaction('rw', db.applicationSession, async () => {
    await db.applicationSession.delete(DEVICE_TOKEN_KEY);
    await db.applicationSession.delete(DEVICE_METADATA_KEY);
  });
}

export async function hasDeviceToken(): Promise<boolean> {
  const token = await getDeviceToken();
  return token !== null && token.length > 0;
}
